import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getSizes, createSize, updateSize, deleteSize } from '../api/categories';
import CategoryModal from '../components/CategoryModal';
import CategoryCard from '../components/CategoryCard';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const SizesPage = () => {
  const [sizes, setSizes] = useState([]);
  const [filteredSizes, setFilteredSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch sizes
  const fetchSizes = async () => {
    setLoading(true);
    try {
      const data = await getSizes();
      setSizes(data);
      setFilteredSizes(data);
    } catch (error) {
      toast.error('Failed to fetch sizes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSizes();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSizes(sizes);
    } else {
      const filtered = sizes.filter((size) =>
        size.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSizes(filtered);
    }
  }, [searchTerm, sizes]);

  // Handle create/update with optimistic UI
  const handleSubmit = async (data) => {
    try {
      if (selectedSize) {
        const updated = await updateSize(selectedSize.id, data);
        setSizes((prev) => prev.map((s) => (s.id === selectedSize.id ? { ...s, ...updated } : s)));
        toast.success('Size updated successfully');
      } else {
        const created = await createSize(data);
        setSizes((prev) => [...prev, created]);
        toast.success('Size created successfully');
      }
      setShowModal(false);
      setSelectedSize(null);
    } catch (error) {
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (size) => {
    setSelectedSize(size);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = (size) => {
    setSelectedSize(size);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteSize(selectedSize.id);
      setSizes((prev) => prev.filter((s) => s.id !== selectedSize.id));
      toast.success('Size deleted successfully');
      setShowDeleteModal(false);
      setSelectedSize(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete size';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-black">Size Management</h1>
              <p className="text-gray-600 mt-1">Manage tile sizes and dimensions</p>
            </div>
            <button
              onClick={() => {
                setSelectedSize(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Size
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search sizes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchSizes}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-accent-gold">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Sizes</h3>
            <p className="text-3xl font-bold text-primary-black">{sizes.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Showing</h3>
            <p className="text-3xl font-bold text-primary-black">{filteredSizes.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Search Results</h3>
            <p className="text-3xl font-bold text-primary-black">
              {searchTerm ? filteredSizes.length : '-'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredSizes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No sizes found matching your search'
                : 'No sizes yet. Add your first size!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSizes.map((size) => (
              <CategoryCard
                key={size.id}
                category={size}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedSize(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedSize}
        title={selectedSize ? 'Edit Size' : 'Add New Size'}
        categoryType="size"
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSize(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedSize?.name}
        loading={deleteLoading}
      />
    </div>
  );
};

export default SizesPage;
